import React from "react";
import { useFormContext } from "react-hook-form";
import { ComponentSize, LabelPlacement } from "@common/types";

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
    (props, ref) => {
        const sizeClasses = {
            xs: "select-xs",
            sm: "select-sm",
            md: "select-md",
            lg: "select-lg",
            xl: "select-xl",
        };

        const sizeClass = props.size ? sizeClasses[props.size] : "";

        // always call the hook, but it returns null if not inside FormProvider
        const formContext = useFormContext();
        const error = formContext?.formState?.errors?.[props.name];

        // use register if form context exists
        // otherwise use traditional props with the forwarded ref
        const selectProps = formContext
            ? formContext.register(props.name, { onChange: props.onChange })
            : {
                ref,
                name: props.name,
                value: props.value,
                onChange: (e: React.ChangeEvent<HTMLSelectElement>) =>
                    props.onChange?.(e),
            };

        return (
            <div className="w-full">
                <label
                    className={`select select-primary border-primary/25 inline-flex ${
                        props.fullWidth ? "w-full" : ""
                    } ${sizeClass} ${props.labelClass}`}
                    htmlFor={props.name}
                >
                    {props.labelPlacement === LabelPlacement.Inside &&
                        props.labelText && (
                        <span className="mr-2">{props.labelText}</span>
                    )}
                    <select
                        className={`select select-primary grow ${sizeClass} -ml-3 placeholder-gray-400 ${props.inputClass}`}
                        id={props.name}
                        disabled={props.disabled}
                        {...selectProps}
                    >
                        {props.options.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    {props.labelPlacement === LabelPlacement.After &&
                        props.labelText && (
                        <p className="label">{props.labelText}</p>
                    )}
                </label>

                {!props.hideError && !!error && (
                    <p className="text-red-500 text-sm mt-1">
                        {error?.message as string}
                    </p>
                )}
            </div>
        );
    },
);

export interface SelectProps {
    name: string;
    options: { value: string; label: string }[];
    value?: string | number;
    onChange?: React.ChangeEventHandler<HTMLSelectElement>;
    inputClass?: string;
    labelClass?: string;
    size?: ComponentSize;
    fullWidth?: boolean;
    labelPlacement?: LabelPlacement;
    labelText?: string;
    disabled?: boolean;
    hideError?: boolean;
}

export default Select;
